// Which fields of a form differ from the record it was opened on (ADR 0101).
//
// One comparison for every place that needs "what did THIS user change": the
// diff-only browser draft (`useFormDraft` with a `baseline`), and a form that
// saves a per-key patch (backoffice-ui's custom-fields form, issue 874). Two
// copies of this rule would drift on exactly the edge that matters — whether a
// cleared input counts as a change — so it lives here once.
//
// The rules:
//   - values compare by their JSON form, so an array or a nested object
//     compares by content rather than by identity;
//   - `''`, `null` and `undefined` are ONE empty value: a text input the user
//     never touched holds `''` while the record says `null`, and calling that a
//     change would resurrect an empty string over a colleague's later value;
//   - `alsoKeys` are reported even when equal — a restored draft key, say, which
//     the user means to save whatever value it now meets.

function normalizeEmpty(value: unknown): unknown {
  return value === '' || value === undefined ? null : value;
}

function sameValue(a: unknown, b: unknown): boolean {
  const left = normalizeEmpty(a);
  const right = normalizeEmpty(b);
  if (left === right) return true;
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

export interface ChangedFieldsOptions {
  /** Keys reported as changed even when their value equals the baseline. */
  alsoKeys?: Iterable<string>;
}

/**
 * The entries of `current` whose value differs from `baseline` (plus any
 * `alsoKeys` present in `current`). Keys that exist only in `baseline` are not
 * reported: a form projection that dropped a field did not clear it.
 */
export function changedFields<T extends Record<string, unknown>>(
  current: T,
  baseline: Record<string, unknown> | null | undefined,
  options: ChangedFieldsOptions = {},
): Partial<T> {
  const base = baseline ?? {};
  const forced = new Set(options.alsoKeys ?? []);
  const out: Partial<T> = {};
  for (const [key, value] of Object.entries(current)) {
    if (forced.has(key) || !sameValue(value, base[key])) {
      (out as Record<string, unknown>)[key] = value;
    }
  }
  return out;
}
